import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import axios from 'axios';
import './App.css';

function App() {
  return (
    <Router>
      <div className="App">
        <header className="App-header">
          <h1>Notes App</h1>
          <nav>
            <ul>
              <li>
                <Link to="/">Home</Link>
              </li>
              <li>
                <Link to="/notes">Notes</Link>
              </li>
            </ul>
          </nav>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/notes" element={<NotesList />} />
            <Route path="/notes/:id" element={<NoteDetail />} />
          </Routes>
        </main>
        <footer>
          <p>&copy; 2025 Notes App</p>
        </footer>
      </div>
    </Router>
  );
}

function Home() {
  const [serverStatus, setServerStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/test')
      .then(response => {
        setServerStatus(response.data);
      })
      .catch(err => {
        setError('Error connecting to server');
        console.error(err);
      });
  }, []);

  return (
    <div className="home-container">
      <h2>Welcome to Notes App</h2>
      <p>A simple application to manage your notes in the cloud.</p>

      <div className="server-status">
        <h3>Server Status</h3>
        {error ? (
          <p className="error">{error}</p>
        ) : serverStatus ? (
          <div>
            <p>Status: {serverStatus.message}</p>
            <p>Server Time: {new Date(serverStatus.time).toLocaleString()}</p>
          </div>
        ) : (
          <p>Loading server status...</p>
        )}
      </div>

      <div className="getting-started">
        <h3>Getting Started</h3>
        <p>Go to the <Link to="/notes">Notes</Link> section to start creating and managing your notes.</p>
      </div>
    </div>
  );
}

function NotesList() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newNote, setNewNote] = useState({ title: '', content: '' });

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = () => {
    setLoading(true);
    axios.get('/api/notes')
      .then(response => {
        setNotes(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError('Error fetching notes');
        setLoading(false);
        console.error(err);
      });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewNote(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newNote.title.trim()) {
      alert('Title is required');
      return;
    }

    axios.post('/api/notes', newNote)
      .then(response => {
        setNotes([response.data, ...notes]);
        setNewNote({ title: '', content: '' });
      })
      .catch(err => {
        alert('Error creating note');
        console.error(err);
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      axios.delete(`/api/notes/${id}`)
        .then(() => {
          setNotes(notes.filter(note => note.id !== id));
        })
        .catch(err => {
          alert('Error deleting note');
          console.error(err);
        });
    }
  };

  if (loading) return <div>Loading notes...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="notes-container">
      <h2>Your Notes</h2>

      <form onSubmit={handleSubmit} className="note-form">
        <h3>Create New Note</h3>
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={newNote.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="content">Content</label>
          <textarea
            id="content"
            name="content"
            value={newNote.content}
            onChange={handleInputChange}
            rows="4"
          />
        </div>
        <button type="submit">Add Note</button>
      </form>

      <div className="notes-list">
        <h3>All Notes ({notes.length})</h3>
        {notes.length === 0 ? (
          <p>No notes yet. Create your first note above!</p>
        ) : (
          <ul>
            {notes.map(note => (
              <li key={note.id} className="note-item">
                <div className="note-header">
                  <h4><Link to={`/notes/${note.id}`}>{note.title}</Link></h4>
                  <div className="note-actions">
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="note-timestamp">
                  Last updated: {new Date(note.updated_at).toLocaleString()}
                </p>
                <p className="note-preview">
                  {note.content?.substring(0, 100)}
                  {note.content?.length > 100 ? '...' : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function NoteDetail() {
  // This is simplified - in a real app, you'd use useParams from react-router-dom
  const noteId = window.location.pathname.split('/').pop();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNote, setEditedNote] = useState({ title: '', content: '' });

  useEffect(() => {
    axios.get(`/api/notes/${noteId}`)
      .then(response => {
        setNote(response.data);
        setEditedNote(response.data);
        setLoading(false);
      })
      .catch(err => {
        setError('Error fetching note');
        setLoading(false);
        console.error(err);
      });
  }, [noteId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedNote(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!editedNote.title.trim()) {
      alert('Title is required');
      return;
    }

    axios.put(`/api/notes/${noteId}`, {
      title: editedNote.title,
      content: editedNote.content
    })
      .then(response => {
        setNote(response.data);
        setIsEditing(false);
      })
      .catch(err => {
        alert('Error updating note');
        console.error(err);
      });
  };

  if (loading) return <div>Loading note...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!note) return <div className="error">Note not found</div>;

  return (
    <div className="note-detail">
      <div className="note-nav">
        <Link to="/notes">&larr; Back to Notes</Link>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="edit-note-form">
          <h2>Edit Note</h2>
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={editedNote.title}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="content">Content</label>
            <textarea
              id="content"
              name="content"
              value={editedNote.content}
              onChange={handleInputChange}
              rows="10"
            />
          </div>
          <div className="form-buttons">
            <button type="submit">Save</button>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditedNote(note);
              }}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="note-content">
          <div className="note-header">
            <h2>{note.title}</h2>
            <button
              onClick={() => setIsEditing(true)}
              className="edit-btn"
            >
              Edit
            </button>
          </div>
          <p className="note-timestamp">
            Created: {new Date(note.created_at).toLocaleString()} |
            Last updated: {new Date(note.updated_at).toLocaleString()}
          </p>
          <div className="note-body">
            {note.content?.split('\n').map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
